import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  signInWithPopup,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../lib/firebase';
import { optimizedApi } from '../utils/apiOptimizer';
import logger from '../lib/logger';

// Global rate limiting for subscription checks
let lastSubscriptionCheck = 0;
let lastProfileLoads: { [key: string]: number } = {};
let subscriptionCheckDebounceTimer: NodeJS.Timeout | null = null;

// Guest mode management
const GUEST_SESSION_KEY = 'guest_session_used';
const GUEST_SESSION_TIMESTAMP = 'guest_session_timestamp';

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  plan: 'free' | 'creator' | 'pro';
  videosProcessed: number;
  monthlyLimit: number;
  createdAt: Date;
  lastActive: Date;
  subscriptionStatus?: string;
  hasActiveSubscription?: boolean;
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;
  storageUsed?: number;
  storageLimit?: number;
}

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  subscriptionLoading: boolean;
  isGuest: boolean;
  hasUsedGuestMode: boolean;
  canUseGuestMode: boolean;
  login: (email: string, password: string) => Promise<User>;
  signup: (email: string, password: string, displayName: string) => Promise<User>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  loginWithGoogle: () => Promise<User>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  verifySubscriptionStatus: () => Promise<void>;
  refreshSubscriptionStatus: () => Promise<void>;
  markGuestModeUsed: () => void;
  enableGuestMode: () => void;
  clearGuestSession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [hasUsedGuestMode, setHasUsedGuestMode] = useState(false);
  const [subscriptionUpdating, setSubscriptionUpdating] = useState(false);

  // Check guest mode state from session storage
  const checkGuestModeState = () => {
    try {
      const guestUsed = sessionStorage.getItem(GUEST_SESSION_KEY) === 'true';
      const guestTimestamp = sessionStorage.getItem(GUEST_SESSION_TIMESTAMP);
      
      // Check if guest session is from the same browser session (within last 24 hours)
      if (guestUsed && guestTimestamp) {
        const timestamp = parseInt(guestTimestamp);
        const now = Date.now();
        const timeDiff = now - timestamp;
        const oneDay = 24 * 60 * 60 * 1000;
        
        if (timeDiff < oneDay) {
          setHasUsedGuestMode(true);
        } else {
          // Clear expired guest session
          sessionStorage.removeItem(GUEST_SESSION_KEY);
          sessionStorage.removeItem(GUEST_SESSION_TIMESTAMP);
        }
      }
    } catch (error) {
      console.warn('Error checking guest mode state:', error);
    }
  };

  // Initialize guest mode state
  React.useEffect(() => {
    checkGuestModeState();
  }, []);

  const canUseGuestMode = !currentUser && !hasUsedGuestMode;

  // Helper function to clear all user-specific data
  const clearUserData = () => {
    // Clear all cached data to prevent showing previous user's data to next user
    optimizedApi.cache.invalidate();
    
    // Clear user-specific localStorage data
    try {
      localStorage.removeItem('upload_tasks');
      localStorage.removeItem('last_restoration_check');
      localStorage.removeItem('has_recent_uploads');
      
      // Clear any processing videos data
      const processingKey = 'processing_videos';
      localStorage.removeItem(processingKey);
      
      // Clear recent task entries (they use video ID patterns)
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('recent_task_') || key.startsWith('video_recent_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Error clearing user data:', error);
    }
  };

  // Enhanced function to load complete user profile with subscription data
  const loadCompleteUserProfile = async (user: User): Promise<UserProfile> => {
    try {
      // Rate limiting: don't allow profile loads more than once per 10 seconds per user
      const now = Date.now();
      const userKey = `profile_${user.uid}`;
      if (lastProfileLoads[userKey] && (now - lastProfileLoads[userKey]) < 10000) {
        // console.log('Rate limiting: User profile loaded recently, using cached data');
        // Return cached profile if available
        if (userProfile && userProfile.uid === user.uid) {
          return userProfile;
        }
      }
      
      // Set rate limit timestamp
      lastProfileLoads[userKey] = now;
      
      const token = await user.getIdToken();
      
      // Fetch complete subscription status first (primary source of truth)
      const subscriptionResponse = await fetch('/api/v1/subscription/subscription-status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let subscriptionData = null;
      if (subscriptionResponse.ok) {
        subscriptionData = await subscriptionResponse.json();
      }

      // Only fetch quota data if subscription data is not available or incomplete
      let quotaData = null;
      if (!subscriptionData || !subscriptionData.plan) {
        const quotaResponse = await fetch('/api/v1/user/quota', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (quotaResponse.ok) {
          quotaData = await quotaResponse.json();
        }
      }

      // Build profile with stable defaults to prevent flickering
      const hasActiveSubscription = subscriptionData?.has_active_subscription || false;
      const plan = subscriptionData?.plan || quotaData?.plan || 'free';
      
      const profile: UserProfile = {
        uid: user.uid,
        email: user.email!,
        displayName: user.displayName || 'Stream Creator',
        photoURL: user.photoURL || undefined,
        plan: plan,
        videosProcessed: subscriptionData?.videos_count || quotaData?.videos_count || 0,
        monthlyLimit: (subscriptionData?.videos_limit || quotaData?.videos_limit || 3) + (quotaData?.referral_bonus_videos || 0),
        createdAt: new Date(),
        lastActive: new Date(),
        subscriptionStatus: subscriptionData?.subscription_status,
        hasActiveSubscription: hasActiveSubscription,
        currentPeriodEnd: subscriptionData?.current_period_end ? new Date(subscriptionData.current_period_end) : undefined,
        cancelAtPeriodEnd: subscriptionData?.cancel_at_period_end || false,
        storageUsed: subscriptionData?.storage_used || 0,
        storageLimit: subscriptionData?.storage_limit || (1024 * 1024 * 1024) // 1GB default
      };

      return profile;
    } catch (error) {
      logger.error('Error loading complete user profile:', error);
      
      // Fallback to basic profile
      return {
        uid: user.uid,
        email: user.email!,
        displayName: user.displayName || 'Stream Creator',
        photoURL: user.photoURL || undefined,
        plan: 'free',
        videosProcessed: 0,
        monthlyLimit: 3,
        createdAt: new Date(),
        lastActive: new Date(),
        hasActiveSubscription: false
      };
    }
  };

  const login = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    const profile = await loadCompleteUserProfile(result.user);
    setUserProfile(profile);
    return result.user;
  };

  const signup = async (email: string, password: string, displayName: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update the display name
    await updateProfile(result.user, {
      displayName: displayName
    });

    const profile = await loadCompleteUserProfile(result.user);
    setUserProfile(profile);
    return result.user;
  };

  const loginWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    const profile = await loadCompleteUserProfile(result.user);
    setUserProfile(profile);
    return result.user;
  };

  const logout = async () => {
    await signOut(auth);
    setUserProfile(null);
    setIsGuest(false);
    clearUserData();
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (!currentUser || !userProfile) return;

    const userRef = doc(db, 'users', currentUser.uid);
    const updatedProfile = { ...userProfile, ...updates };
    
    await setDoc(userRef, {
      ...updatedProfile,
      createdAt: updatedProfile.createdAt.toISOString(),
      lastActive: updatedProfile.lastActive.toISOString()
    }, { merge: true });

    setUserProfile(updatedProfile);
  };

  const refreshUserProfile = async () => {
    if (!currentUser) return;
    
    try {
      setSubscriptionLoading(true);
      const refreshedProfile = await loadCompleteUserProfile(currentUser);
      setUserProfile(refreshedProfile);
      
      // Also update Firestore with the refreshed data
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, {
        ...refreshedProfile,
        createdAt: refreshedProfile.createdAt.toISOString(),
        lastActive: refreshedProfile.lastActive.toISOString()
      }, { merge: true });
      
    } catch (error) {
      logger.error('Error refreshing user profile:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Verify subscription status with Stripe and update profile
  const verifySubscriptionStatus = async () => {
    if (!currentUser) return;
    
    try {
      setSubscriptionLoading(true);
      const response = await fetch('/api/v1/subscription/verify-status', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });
      
      if (response.ok) {
        // After verification, refresh the complete user profile
        await refreshUserProfile();
      }
    } catch (error) {
      logger.error('Error verifying subscription status:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const refreshSubscriptionStatus = async () => {
    if (!currentUser || subscriptionUpdating) return;
    
    // Clear any existing debounce timer
    if (subscriptionCheckDebounceTimer) {
      clearTimeout(subscriptionCheckDebounceTimer);
    }
    
    // Debounce rapid calls - wait 100ms before executing (reduced for better UX)
    return new Promise<void>((resolve) => {
      subscriptionCheckDebounceTimer = setTimeout(async () => {
        // Rate limiting: don't allow calls more than once per 10 seconds
        const now = Date.now();
        if (lastSubscriptionCheck && (now - lastSubscriptionCheck) < 10000) {
          // console.log('Rate limiting: Subscription status checked recently, skipping');
          resolve();
          return;
        }
        
        try {
          setSubscriptionUpdating(true);
          setSubscriptionLoading(true);
          lastSubscriptionCheck = now; // Set rate limit timestamp
          
          const token = await currentUser.getIdToken();
          
          const response = await fetch('/api/v1/subscription/subscription-status', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const subscriptionData = await response.json();
            
            // Update the user profile with latest subscription data
            if (userProfile) {
              const updatedProfile: UserProfile = {
                ...userProfile,
                plan: subscriptionData.plan || 'free',
                videosProcessed: subscriptionData.videos_count || userProfile.videosProcessed,
                monthlyLimit: subscriptionData.videos_limit || userProfile.monthlyLimit,
                subscriptionStatus: subscriptionData.subscription_status,
                hasActiveSubscription: subscriptionData.has_active_subscription || false,
                currentPeriodEnd: subscriptionData.current_period_end ? new Date(subscriptionData.current_period_end) : undefined,
                cancelAtPeriodEnd: subscriptionData.cancel_at_period_end || false,
                storageUsed: subscriptionData.storage_used || userProfile.storageUsed,
                storageLimit: subscriptionData.storage_limit || userProfile.storageLimit,
                lastActive: new Date()
              };
              
              setUserProfile(updatedProfile);
            }
          }
        } catch (error) {
          logger.error('Error refreshing subscription status:', error);
        } finally {
          setSubscriptionLoading(false);
          setSubscriptionUpdating(false);
          resolve();
        }
      }, 100);
    });
  };

  // Guest mode functions
  const markGuestModeUsed = () => {
    try {
      sessionStorage.setItem(GUEST_SESSION_KEY, 'true');
      sessionStorage.setItem(GUEST_SESSION_TIMESTAMP, Date.now().toString());
      setHasUsedGuestMode(true);
    } catch (error) {
      console.warn('Error marking guest mode as used:', error);
    }
  };

  const enableGuestMode = () => {
    setIsGuest(true);
  };

  const clearGuestSession = () => {
    try {
      sessionStorage.removeItem(GUEST_SESSION_KEY);
      sessionStorage.removeItem(GUEST_SESSION_TIMESTAMP);
      setHasUsedGuestMode(false);
      setIsGuest(false);
    } catch (error) {
      console.warn('Error clearing guest session:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          const profile = await loadCompleteUserProfile(user);
          setUserProfile(profile);
        } catch (error) {
          logger.error('Error loading user profile:', error);
        }
      } else {
        setUserProfile(null);
        clearUserData();
      }
      
      setLoading(false);
    });

    // Cleanup function to clear debounce timer
    return () => {
      unsubscribe();
      if (subscriptionCheckDebounceTimer) {
        clearTimeout(subscriptionCheckDebounceTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    subscriptionLoading,
    isGuest,
    hasUsedGuestMode,
    canUseGuestMode,
    login,
    signup,
    logout,
    resetPassword,
    loginWithGoogle,
    updateUserProfile,
    refreshUserProfile,
    verifySubscriptionStatus,
    refreshSubscriptionStatus,
    markGuestModeUsed,
    enableGuestMode,
    clearGuestSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 